import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import React, { ComponentType } from "react";
import { Alert, Box, Stack, styled, Typography } from "@mui/material";
import PageLoader from "../PageLoader/PageLoader";
import { useTbsTenant } from "../../hooks/tbs/useTbsTenant";
import TbsPageLayout from "../../pages/tbs/shared/TbsPageLayout";
import { hasTbsRole, type TbsRole } from "../../utils/tbsTenant";

// The sibling of `AdminGuard` for the Tiendas Biai screens (plan §6.3).
//
// **It is a UX affordance, and the server remains the enforcement.** Every TBS route is gated in the
// Lambda router before any handler runs, and RLS scopes the data underneath that. This exists so a
// cashier is told "no" by a page rather than by a failed request — not so that anything is safe.
//
// It reads the same `tbs_tenants` claim the API's authorizer does (§6.1), so the two agree by
// construction.
//
// **Three answers, not two** (`TBS-79`). "You have not chosen a company yet" is not "you may not
// come in", and answering the first with the second was a dead end: the company selector lives in
// the header, and this component replaced the whole page before any header rendered. On the deployed
// app that made `/gestion` — where `/` now lands — a grey screen with no control on it.
//
// Both refusals are drawn **inside `TbsPageLayout`**, so whatever is being refused still has the
// app's header on it: a company selector, and a way to sign out.

const NO_PERMISSION_TEXT = "No tiene permisos para abrir esta página.";
const NO_PERMISSION_HELP =
  "Si trabaja en otra empresa, cámbiela arriba. Si cree que es un error, pida a un administrador que revise su rol.";
const CHOOSE_TENANT_TEXT = "Elija una empresa para continuar.";
const CHOOSE_TENANT_HELP = "Use el selector de arriba. Todo lo que verá después pertenece a ella.";

const Centered = styled(Box)({ className: "page-layout" });

interface TbsGuardProps {
  component: ComponentType;
  /**
   * The least privileged role that may open this screen. Mirrors the route's `x-role`.
   *
   * Named `requiredRole` rather than `role`, which is what plan §6.3 sketched: `role` on a JSX
   * element is the ARIA attribute, `jsx-a11y/aria-role` rejects `role="cashier"` as an invalid one,
   * and the rule is right that the two meanings should not share a name.
   */
  requiredRole?: TbsRole;
}

/** A message on the app's own chrome, so the header's controls are within reach. */
const GuardMessage: React.FC<{ title: string; help: string; severity: "info" | "warning" }> = ({
  title,
  help,
  severity,
}) => (
  <TbsPageLayout>
    <Stack spacing={1}>
      <Alert severity={severity}>{title}</Alert>
      <Typography variant="body2" color="text.secondary">
        {help}
      </Typography>
    </Stack>
  </TbsPageLayout>
);

export const TbsGuard: React.FC<TbsGuardProps> = ({ component, requiredRole = "cashier" }) => {
  const { user } = useAuth0();
  // The company chosen at runtime, not the one compiled in (APP-1). `useTbsTenant` resolves it
  // synchronously from the token and storage, so the first render already has the right answer —
  // waiting on an effect would render a refusal for one frame, which looks exactly like being
  // denied access.
  const { tenantId, offerChoice } = useTbsTenant();

  if (user && !hasTbsRole(user.tbs_tenants, tenantId, requiredRole)) {
    // **Undecided, not refused.** `rolesForTenant` returns nothing when several memberships exist
    // and none is chosen — deliberately, because the authorizer answers 400 there rather than
    // guessing — so this arrives at the same place as a genuine refusal and must not say the same
    // thing. Choosing lands back on this route with an answer.
    if (offerChoice && !tenantId) {
      return <GuardMessage title={CHOOSE_TENANT_TEXT} help={CHOOSE_TENANT_HELP} severity="info" />;
    }

    return <GuardMessage title={NO_PERMISSION_TEXT} help={NO_PERMISSION_HELP} severity="warning" />;
  }

  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <Centered className="page-layout">
        <PageLoader />
      </Centered>
    ),
  });

  return <Component />;
};
